import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useHelp } from '../../context/HelpContext';
import api, { userService } from '../../services/api';
import LevelsModal from '../../components/ui/LevelsModal';

const ShoppingBagIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>
);

const TYPE_LABELS_FR = { exam: 'Examens', course: 'Cours', examcourse: 'Cours + Examens' };
const TYPE_LABELS_EN = { exam: 'Exams', course: 'Courses', examcourse: 'Courses + Exams' };

const STATUS_CONFIG = {
    Payee: { label: 'Payée', labelEn: 'Paid', bg: 'bg-green-100 dark:bg-green-500/10', text: 'text-green-700 dark:text-green-400' },
    Pending: { label: 'En attente', labelEn: 'Pending', bg: 'bg-yellow-100 dark:bg-yellow-500/10', text: 'text-yellow-700 dark:text-yellow-400' },
    Cancelled: { label: 'Annulée', labelEn: 'Cancelled', bg: 'bg-red-100 dark:bg-red-500/10', text: 'text-red-700 dark:text-red-400' },
};

const InvoiceRow = ({ order, language }) => {
    const typeLabels = language === 'fr' ? TYPE_LABELS_FR : TYPE_LABELS_EN;
    const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.Pending;
    const isPaid = order.status === 'Payee';

    return (
        <div className="bg-white dark:bg-[#121212] rounded-2xl p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 group hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-all border border-gray-200 dark:border-white/5 shadow-sm dark:shadow-none">
            <div className="flex items-center gap-4 md:gap-6 flex-1 min-w-0">
                {/* Badge */}
                <div className="w-12 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/10 flex items-center justify-center text-[10px] font-black text-blue-700 dark:text-blue-400 flex-shrink-0">
                    {order.badge}
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                            {order.label}
                        </span>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${status.bg} ${status.text}`}>
                            {language === 'fr' ? status.label : status.labelEn}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <span>{order.date}</span>
                        <span>·</span>
                        <span>{order.duration}</span>
                        {order.formula && (
                            <>
                                <span>·</span>
                                <span>{typeLabels[order.formula] || order.formula}</span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Amount + Invoice button */}
            <div className="flex items-center gap-4 flex-shrink-0">
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                    ${parseFloat(order.amount || 0).toFixed(2)}
                </span>
                {isPaid && (
                    <a
                        href={order.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-6 py-2 rounded-full bg-blue-100 dark:bg-[#4d79ff] text-blue-700 dark:text-white text-xs font-bold shadow-none dark:shadow-lg dark:shadow-blue-500/20 hover:scale-[1.05] transition-transform active:scale-[0.95]"
                    >
                        {language === 'fr' ? 'Facture' : 'Invoice'}
                    </a>
                )}
            </div>
        </div>
    );
};

const OrderHistory = () => {
    const { language } = useLanguage();
    const { startTour, hasSeenPage, helpPrefs, isPrefsSynced } = useHelp();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showLevelsModal, setShowLevelsModal] = useState(false);

    // Auto-trigger help tour on first visit
    useEffect(() => {
        if (!isPrefsSynced) return;
        if (helpPrefs.globalEnabled && helpPrefs.autoTrigger && !hasSeenPage('order-history') && !loading) {
            const timer = setTimeout(() => startTour('order-history'), 800);
            return () => clearTimeout(timer);
        }
    }, [isPrefsSynced, helpPrefs.globalEnabled, helpPrefs.autoTrigger, loading, hasSeenPage]);

    useEffect(() => {
        const loadOrders = async () => {
            try {
                const ordersData = await userService.getOrders();

                const formattedOrders = ordersData.map((o, idx) => {
                    const isPack = !!o.packVar;
                    const label = isPack
                        ? (o.packVar || 'Pack')
                        : (o.levelFr || o.levelVar?.toUpperCase() || 'Niveau');
                    const badge = isPack
                        ? 'PACK'
                        : (o.levelVar?.toUpperCase()?.substring(0, 6) || 'LVL');

                    return {
                        id: o.idOrder || idx,
                        label,
                        badge,
                        date: new Date(o.subscriptionDate).toLocaleDateString(
                            language === 'fr' ? 'fr-FR' : 'en-US',
                            { day: 'numeric', month: 'short', year: 'numeric' }
                        ),
                        duration: o.duration
                            ? `${o.duration} ${language === 'fr' ? 'mois' : 'months'}`
                            : 'N/A',
                        formula: o.typeOrder || null,
                        amount: o.amount,
                        status: o.orderStatus,
                        invoiceUrl: `${api.defaults.baseURL}/payment/invoice.php?order_id=${o.idOrder}&lang=${language}`,
                    };
                });

                setOrders(formattedOrders);
            } catch (error) {
                console.error("Failed to load orders", error);
            } finally {
                setLoading(false);
            }
        };
        loadOrders();
    }, [language]);

    if (loading) {
        return <div className="text-center py-12 text-gray-500">{language === 'fr' ? 'Chargement...' : 'Loading...'}</div>;
    }

    return (
        <div className="pb-12">
            {/* Header Banner */}
            <div className="relative overflow-hidden rounded-[2rem] p-10 h-64 bg-gradient-to-br from-[#121b5e] via-[#09103d] to-[#050926] border border-white/5 !mb-12 shadow-xl">
                <div className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.5) 50%)', backgroundSize: '4px 100%' }} />

                <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-600 rounded-2xl shadow-lg">
                            <ShoppingBagIcon className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-4xl font-black text-white tracking-tight">
                            {language === 'fr' ? 'Mes achats' : 'My purchases'}
                        </h1>
                    </div>
                    <p className="text-white/60 text-lg max-w-sm leading-snug">
                        {language === 'fr'
                            ? 'Consulte tes factures et gère tes abonnements'
                            : 'View your invoices and manage your subscriptions'}
                    </p>
                </div>

                <div className="absolute right-[-40px] bottom-[-40px] opacity-20">
                    <ShoppingBagIcon className="w-80 h-80 text-blue-500 stroke-[0.5]" />
                </div>
            </div>

            {/* CTA Banner */}
            <div data-help="buy-extend-cta" className="relative overflow-hidden rounded-[1.5rem] bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-white/5 shadow-sm dark:shadow-none">
                <div className="absolute inset-0">
                    <img
                        src="/img/account/buy-bg.png"
                        alt="Background"
                        className="w-full h-full object-cover opacity-20 dark:opacity-40 mix-blend-overlay"
                    />
                </div>
                <div className="relative z-10 flex items-center justify-between p-8">
                    <div className="flex items-center gap-6">
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white leading-tight">
                            {language === 'fr' ? 'Acheter ou prolonger\nun niveau' : 'Buy or extend\na level'}
                        </h2>
                    </div>
                    <button
                        onClick={() => setShowLevelsModal(true)}
                        className="px-10 py-3 rounded-full bg-[#4d79ff] text-white font-bold text-sm shadow-xl shadow-blue-500/20 hover:scale-[1.05] transition-transform active:scale-[0.95]"
                    >
                        {language === 'fr' ? 'Voir les abonnements' : 'View subscriptions'}
                    </button>
                </div>
            </div>

            {/* History Section */}
            <div data-help="order-list" className="space-y-6 !mt-12">
                <h3 className="text-xl font-bold text-gray-500 dark:text-gray-400">
                    {language === 'fr' ? 'Historique des factures' : 'Invoices history'}
                </h3>
                <div className="flex flex-col gap-4">
                    {orders.length > 0 ? (
                        orders.map((order) => (
                            <InvoiceRow key={order.id} order={order} language={language} />
                        ))
                    ) : (
                        <div className="p-12 text-center bg-gray-50 dark:bg-[#121212] rounded-3xl border border-dashed border-gray-200 dark:border-white/10 text-gray-500">
                            {language === 'fr' ? 'Aucune facture trouvée' : 'No invoices found'}
                        </div>
                    )}
                </div>
            </div>

            {showLevelsModal && (
                <LevelsModal onClose={() => setShowLevelsModal(false)} />
            )}
        </div>
    );
};

export default OrderHistory;
